import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys, unwrap, type components } from '@geefamily/api';
import { api } from '@/app/api';
import { useAuth } from '@/features/auth/use-auth';

export type CasinoAccount = components['schemas']['CasinoAccountResponse'];

export const casinoAccountKeys = queryKeys.module('identity').entity('casino-accounts');

export interface SaveCasinoAccountInput {
  siteSlug: string;
  username: string | null;
  externalUserId: string | null;
}

export function useCasinoAccounts() {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: casinoAccountKeys.lists(),
    queryFn: () => unwrap<CasinoAccount[]>(api.GET('/api/v1/identity/me/casino-accounts', {})),
    enabled: isAuthenticated,
  });
}

export function useSaveCasinoAccount() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({ siteSlug, username, externalUserId }: SaveCasinoAccountInput) =>
      unwrap<CasinoAccount>(
        api.PUT('/api/v1/identity/me/casino-accounts/{siteSlug}', {
          params: { path: { siteSlug } },
          body: { username, externalUserId },
        }),
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: casinoAccountKeys.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.module('races').all }),
        // Milestones resolve the current saved partner account immediately.
        // Refresh the mounted profile card as well as an already cached roadmap.
        queryClient.invalidateQueries({
          queryKey: queryKeys
            .module('market')
            .entity('me')
            .detail(user?.id ?? ''),
        }),
        // Ticket pages render the saved identifier and open the form from it.
        // Without this an event already on screen kept claiming the member had
        // saved nothing until its 30s stale time elapsed.
        queryClient.invalidateQueries({ queryKey: queryKeys.module('tickets').all }),
      ]),
  });
}

export function useRemoveCasinoAccount() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (siteSlug: string) =>
      unwrap<void>(
        api.DELETE('/api/v1/identity/me/casino-accounts/{siteSlug}', {
          params: { path: { siteSlug } },
        }),
      ),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: casinoAccountKeys.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.module('races').all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys
            .module('market')
            .entity('me')
            .detail(user?.id ?? ''),
        }),
        // Ticket pages render the saved identifier and open the form from it.
        // Without this an event already on screen kept claiming the member had
        // saved nothing until its 30s stale time elapsed.
        queryClient.invalidateQueries({ queryKey: queryKeys.module('tickets').all }),
      ]),
  });
}
